/**
 * Appendix A lottery lifecycle actions wired to the admin client. Each action names its permission (§3), its
 * target state (LotteryMachine decides availability), whether it is destructive (typed confirmation, §16.3),
 * whether a reason is mandatory (pause / cancel) and whether it steps up (`lottery.cancel`).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LotteryMachine, type LotteryStatus, type Permission } from '@windrop/contracts';
import { Button, Field, Input, Modal, useToast } from '@windrop/ui';
import { useState } from 'react';
import { useSession } from '../../auth/session';
import { StepUpCancelled, useStepUp } from '../../auth/step-up';
import { ConfirmDestructive, type DryRunResult } from '../../components/ConfirmDestructive';
import { lotteriesApi, type LifecycleAction } from '../../lib/api/admin';
import { isApiError } from '../../lib/api/client';

export interface LifecycleActionDef {
  id: LifecycleAction;
  label: string;
  to: LotteryStatus;
  permission: Permission;
  /** Typed confirmation of the slug. */
  destructive?: boolean;
  requiresReason?: boolean;
  stepUp?: boolean;
  consequence: string;
  variant?: 'primary' | 'secondary' | 'danger';
}

export const LIFECYCLE_ACTIONS: readonly LifecycleActionDef[] = [
  {
    id: 'submit-review',
    label: 'Submit for review',
    to: 'REVIEW',
    permission: 'lottery.submit_review',
    consequence:
      'An approver is asked to review the configuration. Editing continues to be possible until approval.',
    variant: 'primary',
  },
  {
    id: 'approve',
    label: 'Approve',
    to: 'APPROVED',
    permission: 'lottery.approve',
    consequence:
      'Marks the configuration as approved. Paid and high-value lotteries need a second person (§5.4).',
    variant: 'primary',
  },
  {
    id: 'publish',
    label: 'Publish',
    to: 'SCHEDULED',
    permission: 'lottery.publish',
    consequence:
      'Commits the fairness seed, freezes the published version and opens the participant page at the scheduled time.',
    variant: 'primary',
  },
  {
    id: 'pause',
    label: 'Pause entries',
    to: 'PAUSED',
    permission: 'lottery.pause',
    requiresReason: true,
    consequence: 'Participants see the paused status copy; no new entries are accepted until resumed.',
    variant: 'secondary',
  },
  {
    id: 'resume',
    label: 'Resume',
    to: 'LIVE',
    permission: 'lottery.pause',
    consequence: 'Entries open again immediately.',
    variant: 'secondary',
  },
  {
    id: 'close',
    label: 'Close entries',
    to: 'CLOSED',
    permission: 'lottery.close',
    destructive: true,
    consequence: 'Entries close now and the eligible-entry snapshot is taken. This cannot be reopened.',
    variant: 'danger',
  },
  {
    id: 'cancel',
    label: 'Cancel lottery',
    to: 'CANCELLED',
    permission: 'lottery.cancel',
    destructive: true,
    requiresReason: true,
    stepUp: true,
    consequence:
      'Every confirmed paid entry is refunded under the refund rule; participants receive the cancellation template. No draw will run.',
    variant: 'danger',
  },
];

export const availableActions = (
  status: LotteryStatus,
  can: (p: Permission) => boolean,
): LifecycleActionDef[] =>
  LIFECYCLE_ACTIONS.filter((a) => LotteryMachine.canTransition(status, a.to) && can(a.permission));

export interface LifecycleActionsProps {
  lotteryId: string;
  slug: string;
  status: LotteryStatus;
  /** Count of affected participants for the destructive dialogs; omitted = fetched from the draw precheck. */
  affected?: DryRunResult | null;
  onChanged?: (status: LotteryStatus) => void;
}

export function LifecycleActions({ lotteryId, slug, status, affected, onChanged }: LifecycleActionsProps) {
  const { can } = useSession();
  const { withStepUp } = useStepUp();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [active, setActive] = useState<LifecycleActionDef | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async ({ action, reason }: { action: LifecycleActionDef; reason?: string }) => {
      const run = () =>
        action.id === 'cancel'
          ? lotteriesApi.cancel(lotteryId, { reason: reason ?? '', confirmSlug: slug })
          : lotteriesApi.transition(lotteryId, action.id, reason ? { reason } : {});
      return action.stepUp
        ? withStepUp(run, { permission: action.permission, reason: `${action.label} “${slug}”.` })
        : run();
    },
    onSuccess: (res, { action }) => {
      toast({
        title: `${action.label}: done`,
        description: `${slug} is now ${res.to.toLowerCase()}.`,
        tone: 'success',
      });
      void queryClient.invalidateQueries({ queryKey: ['lotteries'] });
      void queryClient.invalidateQueries({ queryKey: ['lottery', lotteryId] });
      setActive(null);
      setReason('');
      onChanged?.(res.to);
    },
    onError: (e) => {
      if (e instanceof StepUpCancelled) return;
      setError(isApiError(e) ? e.message : 'The action did not complete. Nothing changed.');
    },
  });

  const precheck = useQuery({
    queryKey: ['lottery', lotteryId, 'precheck'],
    queryFn: () => lotteriesApi.precheck(lotteryId),
    enabled: affected === undefined && active?.destructive === true,
    retry: false,
  });
  const resolvedAffected: DryRunResult | null | undefined =
    affected !== undefined
      ? affected
      : !active?.destructive
        ? undefined
        : precheck.isPending
          ? null
          : precheck.data
            ? {
                affected: precheck.data.counts.eligible + precheck.data.counts.pending,
                detail: `${precheck.data.counts.eligible} eligible, ${precheck.data.counts.pending} pending payment`,
              }
            : { affected: null, detail: 'the draw precheck could not be reached' };

  const actions = availableActions(status, can);
  if (actions.length === 0) return null;

  const open = (a: LifecycleActionDef) => {
    setError(null);
    setReason('');
    setActive(a);
  };

  return (
    <>
      <div className="wd-btn-group" data-testid="lifecycle-actions">
        {actions.map((a) => (
          <Button
            key={a.id}
            variant={a.variant ?? 'secondary'}
            onClick={() => open(a)}
            data-testid={`lifecycle-${a.id}`}
          >
            {a.label}
          </Button>
        ))}
      </div>
      {active?.destructive ? (
        <ConfirmDestructive
          open
          onOpenChange={(o) => {
            if (!o) setActive(null);
          }}
          title={active.label}
          objectName={slug}
          objectKind="lottery slug"
          consequence={active.consequence}
          affected={resolvedAffected}
          requireReason={active.requiresReason}
          busy={mutation.isPending}
          error={error}
          onConfirm={({ reason }) => mutation.mutate({ action: active, reason })}
        />
      ) : active ? (
        <Modal
          open
          onOpenChange={(o) => {
            if (!o) setActive(null);
          }}
          title={`${active.label} “${slug}”?`}
          description={active.consequence}
        >
          <div className="wd-stack">
            {active.requiresReason ? (
              <Field label="Reason (shown to the approver and kept in the audit trail)" required>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
              </Field>
            ) : null}
            {error ? (
              <p role="alert" className="text-error">
                {error}
              </p>
            ) : null}
            <div className="wd-btn-group">
              <Button
                variant={active.variant === 'danger' ? 'danger' : 'primary'}
                loading={mutation.isPending}
                disabled={active.requiresReason && reason.trim().length < 3}
                disabledReason={
                  active.requiresReason && reason.trim().length < 3
                    ? 'A reason of at least 3 characters is required.'
                    : undefined
                }
                onClick={() =>
                  mutation.mutate({
                    action: active,
                    reason: active.requiresReason ? reason.trim() : undefined,
                  })
                }
              >
                {active.label}
              </Button>
              <Button variant="secondary" onClick={() => setActive(null)}>
                Not now
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </>
  );
}
